<template>
    <div>
        <v-label
            class="text-body-large my-2"
        >
            {{ selectedColor?.value.name }}
        </v-label>
        <v-item-group
            :model-value="selectedColor"
            class="option-btn-group-v2 option-btn-color"
            :class="btnGridColsClass"
            :disabled="disabled"
            @update:model-value="onChange($event)"
            mandatory
            v-bind="$attrs"
        >
            <!--
                NOTE: using "value === modelValue" vs "isSelected" is a workaround for a bug in vuetify
                where the isSelected value is not updated when page hydration occurs. Ignore the TS error.
            -->
            <template
                v-for="(opt, index) in option.groupValues"
                :key="`item-${index}`"
            >
                <v-item
                    v-if="!optionsDisabledModelSet?.has(opt.value.uid)"
                    :value="opt"
                    #default="{ isSelected, toggle, value }"
                >
                    <ColorImageButton
                        v-if="opt.value.svg"
                        :key="`option-color-image-${index}`"
                        :model-value="value === selectedColor"
                        :image="opt.value.svg"
                        :aria-label="opt.value.name"
                        @click="toggle"
                    ></ColorImageButton>
                    <ColorSwatchButton
                        v-else
                        :key="`option-color-swatch-${index}`"
                        :model-value="value === selectedColor"
                        :color="opt.value.colors"
                        :aria-label="opt.value.name"
                        @click="toggle"
                    ></ColorSwatchButton>
                </v-item>
            </template>
        </v-item-group>
    </div>
</template>

<script lang="ts" setup>
    import ColorSwatchButton from '~/components/color/ColorSwatchButton/ColorSwatchButton.vue';
    import ColorImageButton from '~/components/color/ColorImageButton/ColorImageButton.vue';
    import type {
        ProductAttrsSkuOption,
        ProductAttrsGroupOption
    } from '~/types';
    import { useDisplay } from 'vuetify';

    const props = defineProps<{
        modelValue: string;
        option: ProductAttrsGroupOption;
        optionsDisabledModelSet?: Nilish<Set<string>>;
        disabled?: boolean;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: string];
    }>();

    const useDisplayObj = useDisplay();
    const selectedColor = ref<Nullable<ProductAttrsSkuOption>>();
    const btnGridColsClass = computed<string>(() => {
        switch (useDisplayObj.name.value) {
            case 'sm':
                return 'g-col-3';
            case 'xs':
                return 'g-col-2';
            default:
                return '';
        }
    });

    const {
        stop: stopWatchModelValue,
        pause: pauseWatchModelValue,
        resume: resumeWatchModelValue
    } = watch(
        () => props.modelValue,
        (newVal: string) => {
            let val: Nullable<ProductAttrsSkuOption> = null;

            if (newVal) {
                val = props.option.groupValues.find((item) => item.value.uid === newVal) ?? null;
            }

            selectedColor.value = val;
        },
        { immediate: true }
    );

    function onChange(value: Nullable<ProductAttrsSkuOption>) {
        if (!props.disabled && value) {
            selectedColor.value = value;
            const uid = value.value.uid;
            pauseWatchModelValue();
            emit('update:modelValue', uid);
            setTimeout(() => resumeWatchModelValue(), 300);
        }
    }

    onBeforeMount(() => {
        resumeWatchModelValue();
    });

    onBeforeUnmount(() => {
        stopWatchModelValue();
    });
</script>

<style lang="scss"></style>
